import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  AvatarInput,
  BoxExpand,
  Button,
  ChangePassword,
  LogoutButton,
  PageLayout,
  Separator,
  SupportRequestsTable,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';
import { SubscriptionTypesEnum } from '@thinknet-gmbh/platform-common';

import { PASSWORD_PATTERN } from 'src/pages/auth/auth.constants';
import { useChangePassword } from 'src/hooks/accounts/change-password.hook';
import { useAuth } from 'src/hooks/auth.hooks';
import { useGetCurrentAccount } from 'src/hooks/accounts/get-current-account.hook';
import { useUploadProfileAvatar } from 'src/hooks/profiles/upload-profile-avatar.hook';
import { ROUTES } from 'src/components/app/app-routes.constants';
import { EMPLOYEE_PAGES } from 'src/pages/employee/employee.constants';
import { useGetSupportRequests } from 'src/hooks/support-requests/get-support-requests.hook';
import { useUpdateSupportRequest } from 'src/hooks/support-requests/update-support-request.hook';
import { useGetCurrentMemberAccount } from 'src/hooks/accounts/get-current-member-account.hook';
import { useGetCurrentSubscription } from 'src/hooks/subscriptions/get-current-subscription.hook';

import { HeaderContainer, MainContainer, SupportTableWrapper } from './user-profile.styles';
import { UserInfo } from './components/user-info';
import { PaymentInformation } from './components/payment-information';
import { UserAbsence } from './components/user-absence';

const UserProfile: React.FC = () => {
  const { t } = useTranslation(['common', 'settings']);
  const navigate = useNavigate();

  const { account, profile } = useGetCurrentAccount();
  const { member } = useGetCurrentMemberAccount();
  const { subscription } = useGetCurrentSubscription();
  const { loading, paginationConfig, supportRequests } = useGetSupportRequests();
  const updateSupportRequest = useUpdateSupportRequest();

  const uploadProfileAvatar = useUploadProfileAvatar();

  const changePassword = useChangePassword();
  const { logout } = useAuth();

  const onChangePasswordSubmit = (values: any) => {
    changePassword({ variables: values });
  };

  const onAvatarChange = (avatar: any) => {
    uploadProfileAvatar({ variables: { avatar } });
  };

  return (
    <PageLayout pageTitle={t('settings:userProfileSettings.labels.myProfile')}>
      <MainContainer>
        <HeaderContainer>
          <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
            {t('common:labels.avatar')}
          </Typography>
          <Button
            type="primary"
            ghost
            onClick={() =>
              navigate(`${ROUTES.employee}?type=${EMPLOYEE_PAGES.employeeProfile}&id=${member._id}`)
            }
          >
            {t('settings:userProfileSettings.labels.fullView')}
          </Button>
        </HeaderContainer>
        <AvatarInput onChange={onAvatarChange} avatar={profile.avatarUrl} />
        <div>
          <UserInfo data={{ account, profile }} />
          <BoxExpand
            title={t('settings:userProfileSettings.labels.changePassword')}
            separator
            titleFontSize={TYPOGRAPHY_SIZE.medium}
          >
            <ChangePassword
              t={t}
              onSubmitForm={onChangePasswordSubmit}
              passwordPattern={PASSWORD_PATTERN}
              hideTitle
            />
          </BoxExpand>
          {subscription.subscriptionType !== SubscriptionTypesEnum.INTERNAL && (
            <PaymentInformation />
          )}
          <UserAbsence />
          <SupportTableWrapper>
            <BoxExpand
              separator
              title={t('settings:supportTab.mySupportRequest')}
              titleFontSize={TYPOGRAPHY_SIZE.medium}
            >
              <SupportRequestsTable
                t={t}
                loading={loading}
                data={supportRequests}
                paginationConfig={paginationConfig}
                onChangeStatus={(status, _id) =>
                  updateSupportRequest({ variables: { payload: { _id, status } } })
                }
              />
            </BoxExpand>
          </SupportTableWrapper>
          <Separator />
          <LogoutButton t={t} logout={logout} />
        </div>
      </MainContainer>
    </PageLayout>
  );
};

export default UserProfile;
